import { LitElement, css, html, nothing } from "lit";
import { neutral } from "../../legl-ui/lds-colours";
import "../../legl-ui/lds-icon";
import { spacing } from "../../legl-ui/lds-spacing";
import "../../legl-ui/lds-spinner";
import { typographyPresets } from "../../legl-ui/lds-typography";
import { toastService } from "../../legl-ui/toast";
import "../../legl-ui/tooltip";
import { get, put } from "../../legl-ui/utils/fetch";
import { RenderPayment } from "./services/render-payment";

const errorMessage = "Something went wrong. Please try again later.";

export class PaymentWrapper extends LitElement {
  static get styles() {
    return css`
            :host {
                text-align: left;
            }

            .providers-selection h4 {
                ${typographyPresets.h4};
                font-weight: 400;
                margin: ${spacing.s} 0;
                color: ${neutral["600"]};
            }

            .providers-wrapper button {
                display: block;
                width: 100%;
                background-color: transparent;
                border: 1px solid ${neutral["200"]};
                border-bottom: none;
                text-align: left;
                padding: ${spacing.s};
                display: flex;
                align-items: center;
                justify-content: space-between;
                color: ${neutral["800"]};
            }

            .providers-wrapper button:first-child {
                border-radius: 4px 4px 0 0;
            }

            .providers-wrapper button:last-child {
                border-radius: 0 0 4px 4px;
                border-bottom: 1px solid ${neutral["200"]};
            }

            .providers-wrapper button:hover {
                cursor: pointer;
                background-color: ${neutral["100"]};
            }

            .icon-label-wrapper {
                display: flex;
                align-items: center;
            }

            .icon-label-wrapper lds-icon {
                color: ${neutral["700"]};
                margin-right: ${spacing.s};
            }

            .provider-label {
                ${typographyPresets.body};
            }

            .provider-title-wrapper {
                display: flex;
                margin-bottom: ${spacing.m};
                align-items: end;
            }

            .provider-title-wrapper button {
                border: none;
                background-color: transparent;
                ${typographyPresets.small};
                text-decoration: underline;
                color: ${neutral["700"]};
            }

            .provider-title-wrapper button:hover {
                cursor: pointer;
            }

            .provider-title {
                display: flex;
                align-items: center;
                margin-right: ${spacing.xs};
            }

            .provider-title h4 {
                ${typographyPresets.h4};
                margin: 0;
            }

            .provider-title lds-icon {
                margin-right: ${spacing.xs};
            }

            lds-spinner {
                margin-right: ${spacing.s};
            }
        `;
  }

  static get properties() {
    return {
      paymentReference: { type: String, attribute: "payment-reference" },
      locale: { type: String },
      providers: { state: true },
      hasMultipleMethods: { state: true },
      selectedMethod: { state: true },
      loadingPaymentProvider: { state: true },
      paymentMethod: { state: true },
    };
  }

  createRenderRoot() {
    return this;
  }

  async connectedCallback() {
    super.connectedCallback();
    this.addEventListener("providerSelected", this.setPaymentRenderer);
    this.addEventListener(
      "resetProviderSelection",
      this.resetProviderSelection,
    );

    if (this.paymentReference) {
      await this.getPaymentInfo();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("providerSelected");
  }

  async getProviderDetails(method) {
    const requestBody = {
      provider: method.provider,
      method: method.method,
    };
    const res = await put(this.sessionUrl, {
      body: JSON.stringify(requestBody),
    });
    if (res.ok) {
      const data = await res.json();
      this.providerDetails = data.provider_details;
      this.providerDetails.paymentUrl = this.paymentUrl;
    } else {
      toastService.showError(errorMessage);
    }
  }

  async updatePayment(reference) {
    /* When the payment is changed by the first form in the generic portal, this
           method will be called so we can get the latest data, particularly the amount.
        */
    this.paymentReference = reference;
    this.getPaymentInfo();
  }

  async getPaymentInfo() {
    this.paymentMethod = null;
    this.selectedMethod = null;
    const res = await get(`/api/payments/${this.paymentReference}/`);
    if (res.ok) {
      const data = await res.json();
      this.providers = data.providers;
      this.amount = data.amount;
      this.sessionUrl = data.session_url;
      this.paymentUrl = data.url;
    } else {
      toastService.showError(errorMessage);
    }
  }

  resetProviderSelection() {
    this.selectedMethod = null;
    this.paymentMethod = null;
    this.loadingPaymentProvider = false;
  }

  async setPaymentRenderer(event) {
    if (event.detail === null) {
      this.paymentMethod = null;
      this.loadingPaymentProvider = false;
      return;
    }

    this.loadingPaymentProvider = true;

    try {
      this.selectedMethod = event.detail.payment_method;
      this.paymentMethod = event.detail.payment_method;
      await this.getProviderDetails(event.detail);

      await RenderPayment.render(
        this.paymentMethod,
        this.amount,
        this.providerDetails,
      );
    } catch (e) {
      console.log("setPaymentRenderer", e);
    } finally {
      this.loadingPaymentProvider = false;
    }
  }

  // once payment method is select we should render it
  // if nothing is selected then we dont render a payment method
  render() {
    return html` <div>
            <legl-payment-selector
                .providers=${this.providers}
                .selectedMethod=${this.selectedMethod}
            ></legl-payment-selector>
            ${
              this.loadingPaymentProvider
                ? html`<lds-spinner></lds-spinner>`
                : nothing
            }
            ${
              this.paymentMethod
                ? html`<div id="payment-method-component"></div>`
                : nothing
            }
        </div>`;
  }
}

if (!customElements.get("legl-payment-wrapper")) {
  customElements.define("legl-payment-wrapper", PaymentWrapper);
}
